import React, { useState, useEffect } from 'react';
import { reviewsAPI, Review } from '../../api/api';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import './AdminPanel.css';

export const ReviewsAdmin: React.FC = () => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<number | null>(null);

    // Обработчик клика на кнопку удаления
    const handleDeleteClick = (id: number) => {
        setItemToDelete(id);
        setShowConfirm(true);
    };

    // Обработчик подтверждения удаления
    const handleConfirmDelete = async () => {
        if (itemToDelete) {
            try {
                await reviewsAPI.delete(itemToDelete);
                loadReviews();
            } catch (err) {
                alert('Ошибка при удалении');
                console.error(err);
            } finally {
                setShowConfirm(false);
                setItemToDelete(null);
            }
        }
    };

    // Обработчик отмены удаления
    const handleCancelDelete = () => {
        setShowConfirm(false);
        setItemToDelete(null);
    };

    // Форма
    const [formData, setFormData] = useState({
        name_reviews: '',
        text_reviews: '',
        image_reviews: null as File | null,
    });

    // Загрузка отзывов
    useEffect(() => {
        loadReviews();
    }, []);

    const loadReviews = async () => {
        try {
            setLoading(true);
            const data = await reviewsAPI.getAll();
            setReviews(data);
            setError(null);
        } catch (err) {
            setError('Не удалось загрузить отзывы');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Обработка ввода
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFormData(prev => ({ ...prev, image_reviews: e.target.files![0] }));
        }
    };

    // Добавление/редактирование
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name_reviews || !formData.text_reviews) {
            alert('Заполните все поля!');
            return;
        }

        const submitData = new FormData();
        submitData.append('name_reviews', formData.name_reviews);
        submitData.append('text_reviews', formData.text_reviews);
        if (formData.image_reviews) {
            submitData.append('image_reviews', formData.image_reviews);
        }

        try {
            if (editingId) {
                submitData.append('id', editingId.toString());
                await reviewsAPI.update(editingId, submitData);
            } else {
                await reviewsAPI.add(submitData);
            }

            // Очистка формы и перезагрузка
            setFormData({ name_reviews: '', text_reviews: '', image_reviews: null });
            setShowForm(false);
            setEditingId(null);
            loadReviews();
        } catch (err) {
            alert('Ошибка при сохранении');
            console.error(err);
        }
    };

    // Редактирование
    const handleEdit = (review: Review) => {
        setFormData({
            name_reviews: review.name_reviews,
            text_reviews: review.text_reviews,
            image_reviews: null,
        });
        setEditingId(review.id);
        setShowForm(true);
    };

    // Отмена редактирования
    const cancelEdit = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({ name_reviews: '', text_reviews: '', image_reviews: null });
    };

    if (loading) return <div>Загрузка...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="admin-panel">
            <div className="admin-panel-header">
                <h2>Управление отзывами</h2>
                <button
                    className="btn-add"
                    onClick={() => {
                        setShowForm(!showForm);
                        setEditingId(null);
                        setFormData({ name_reviews: '', text_reviews: '', image_reviews: null });
                    }}
                >
                    {showForm ? 'Отмена' : '+ Добавить отзыв'}
                </button>
            </div>

            {/* Форма добавления/редактирования */}
            {showForm && (
                <form className="review-form" onSubmit={handleSubmit}>
                    <h3>{editingId ? 'Редактировать отзыв' : 'Новый отзыв'}</h3>

                    <div className="form-group">
                        <label>Имя:</label>
                        <input
                            type="text"
                            name="name_reviews"
                            value={formData.name_reviews}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Текст отзыва:</label>
                        <textarea
                            name="text_reviews"
                            value={formData.text_reviews}
                            onChange={handleInputChange}
                            rows={4}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Изображение:</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                        />
                        {formData.image_reviews && (
                            <span className="file-info">Выбран файл: {formData.image_reviews.name}</span>
                        )}
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn-save">
                            {editingId ? 'Сохранить' : 'Добавить'}
                        </button>
                        <button type="button" className="btn-cancel" onClick={cancelEdit}>
                            Отмена
                        </button>
                    </div>
                </form>
            )}

            {/* Список отзывов */}
            <div className="reviews-list">
                {reviews.length === 0 ? (
                    <p>Нет отзывов</p>
                ) : (
                    reviews.map(review => (
                        <div key={review.id} className="review-item-admin">
                            {review.image_reviews_base64 && (
                                <img
                                    src={`data:image/jpeg;base64,${review.image_reviews_base64}`}
                                    alt={review.name_reviews}
                                    className="review-image"
                                />
                            )}
                            <div className="review-content">
                                <h4>{review.name_reviews}</h4>
                                <p>{review.text_reviews}</p>
                            </div>
                            <div className="review-actions">
                                <button
                                    className="btn-edit"
                                    onClick={() => handleEdit(review)}
                                >
                                    ✏️
                                </button>
                                <button
                                    className="btn-delete"
                                    onClick={() => handleDeleteClick(review.id)}
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Диалог подтверждения удаления */}
            {showConfirm && itemToDelete && (
                <ConfirmDialog
                    message="Удалить этот отзыв?"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            )}
        </div>
    );
};